'use client';

import React, { FC, useEffect, useRef, useState } from 'react';
import { TbArrowsMaximize, TbPlayerPauseFilled, TbPlayerPlayFilled } from 'react-icons/tb';

import { Box, Flex, Modal } from '@mantine/core';

import { ScrollingImage } from '@/../marketing/components/scrolling-image/ScrollingImage';
import { Feature } from '@/../marketing/data/features';

import cx from './FeaturePreview.module.scss';

interface FeaturePreviewProps {
    feature: Feature;
    aspectRatio?: number;
}

const FeaturePreview: FC<FeaturePreviewProps> = ({ feature, aspectRatio = 4 / 3 }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);

    useEffect(() => {
        const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const handleFullscreenToggle = () => {
        if (videoRef.current) {
            videoRef.current.requestFullscreen();
            return;
        }

        setIsImageModalOpen(true);
    };

    const handlePlayPauseToggle = () => {
        if (!videoRef.current) return;

        if (isPlaying) {
            videoRef.current.pause();
        } else {
            videoRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const imageSrc =
        feature.preview?.type === 'image' || feature.preview?.type === 'scrolling-image'
            ? feature.preview.image.src
            : null;

    return (
        <Flex className={cx.visualContainer} align="center" justify="center" style={{ aspectRatio }}>
            {feature?.preview ? (
                <Flex className={cx.previewWrapper} align="center" justify="center">
                    {feature.preview.type === 'image' && (
                        <Box
                            component="img"
                            src={feature.preview.image.src}
                            className={cx.previewMedia}
                            style={{ objectFit: feature.preview.fit ?? 'cover' }}
                        />
                    )}
                    {feature.preview.type === 'scrolling-image' && (
                        <ScrollingImage image={feature.preview.image} autoplay aspectRatio={aspectRatio} />
                    )}
                    {feature.preview.type === 'video' && (
                        <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            loop
                            controls={isFullscreen}
                            className={cx.previewMedia}
                            style={{ objectFit: feature.preview.fit ?? 'cover' }}
                        >
                            {feature.preview.sources.map((source) => (
                                <source key={source.src} src={source.src} type={source.type} />
                            ))}
                        </video>
                    )}
                    {feature.preview.type === 'text' && <Box className={cx.previewText}>{feature.preview.text}</Box>}
                    {feature.preview.type === 'html' && <Box w="100%">{feature.preview.html}</Box>}

                    {(feature.preview.type === 'video' ||
                        feature.preview.type === 'image' ||
                        feature.preview.type === 'scrolling-image') && (
                        <Flex className={cx.controls} align="center" gap="xs">
                            {feature.preview.type === 'video' && (
                                <Box className={cx.controlButton} onClick={handlePlayPauseToggle}>
                                    {isPlaying ? <TbPlayerPauseFilled /> : <TbPlayerPlayFilled />}
                                </Box>
                            )}
                            <Box className={cx.controlButton} onClick={handleFullscreenToggle}>
                                <TbArrowsMaximize />
                            </Box>
                        </Flex>
                    )}
                </Flex>
            ) : (
                <Flex className={cx.noPreview} align="center" justify="center">
                    {feature.icon && <Box className={cx.fallbackIcon}>{feature.icon}</Box>}
                </Flex>
            )}

            {(feature.preview?.type === 'image' || feature.preview?.type === 'scrolling-image') && (
                <Modal
                    opened={isImageModalOpen}
                    onClose={() => setIsImageModalOpen(false)}
                    size="85vw"
                    centered
                    padding={0}
                    withCloseButton={false}
                    overlayProps={{ backgroundOpacity: 0.55, blur: 5 }}
                    styles={{
                        inner: { pointerEvents: 'none' },
                        content: { background: 'transparent', boxShadow: 'none', pointerEvents: 'none' },
                        body: { padding: 0, pointerEvents: 'none' },
                    }}
                >
                    <Box
                        component="img"
                        src={imageSrc ?? ''}
                        style={{
                            display: 'block',
                            margin: 'auto',
                            maxHeight: '85vh',
                            objectFit: 'contain',
                            pointerEvents: 'all',
                        }}
                    />
                </Modal>
            )}
        </Flex>
    );
};

export { FeaturePreview };
